import { useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { Box, NotFound } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout, useAiChat } from '@factorialco/f0-react'
import { BaseBanner } from '@factorialco/f0-react/dist/experimental'
import {
  CanSeeModulesMarketplaceDocument,
  CanSelfUpsellDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'
import { ModuleCarousel } from 'modules/marketplace_modules/components/Revamp/Carousel'
import ClientReviews from 'modules/marketplace_modules/components/Revamp/ClientReviews'
import FeatureBlocks from 'modules/marketplace_modules/components/Revamp/FeatureBlocks'
import LatestUpdates from 'modules/marketplace_modules/components/Revamp/LatestUpdates'
import PricingPlan from 'modules/marketplace_modules/components/Revamp/PricingPlan'
import SidePanel from 'modules/marketplace_modules/components/Revamp/SidePanel'
import TrainingsBanner from 'modules/marketplace_modules/components/TrainingsBanner'
import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import { SelfUpsellProvider } from 'modules/upsell/store/SelfUpsellProvider'
import { useLocalStorageWithExpiration } from 'modules/upselling_kit/utils/localStorage'

const UPSELL_ORIGIN_STORAGE_KEY = 'upsell_origin'

const optimizeImageUrl = (url: string, width = 800): string => {
  if (!url) return url
  try {
    const parsed = new URL(url)
    if (parsed.hostname.endsWith('.datocms-assets.com') || parsed.hostname.endsWith('.imgix.net')) {
      parsed.searchParams.set('w', String(width))
      parsed.searchParams.set('auto', 'format')
      return parsed.toString()
    }
  } catch {
    // Not a valid URL, return as-is
  }
  return url
}

const ModulesDetailRevamp = () => {
  const { id: slug } = useParams()
  const { search } = useLocation()
  const queryParams = new URLSearchParams(search)
  const showAiChat = queryParams.get('showAiChat')

  const {
    company,
    access: { locale: user_locale },
  } = useCurrentSession()
  const { data: policies } = useQuery(CanSeeModulesMarketplaceDocument)
  const { data: selfUpsellPolicies } = useQuery(CanSelfUpsellDocument)

  const { sendMessage, setOpen, open } = useAiChat()

  useEffect(() => {
    if (showAiChat && !open) {
      setOpen(true)
      sendMessage(
        i18n.t('growth.one_entrypoints.exploring_message', {
          module: moduleData[0]?.translatedName,
        })
      )
    }
  }, [])

  const datoFormatLocale = getDatoLocale(user_locale)
  const countryCodeForCompany = countryCode(company.legalEntities[0]?.country || '')
  const isAuthorized =
    policies?.modules._policies.moduleManageModule.can && !company.subscription.inFreeTrial

  const isFreeTrial = company.subscription.inFreeTrial
  const canSubscriptionUpgrade =
    selfUpsellPolicies?.billing._policies.subscriptionUpgradeSubscription.can
  const canUpsell = policies?.modules._policies.moduleManageModule.can

  const canSeeSelfUpsell = !isFreeTrial && !!canSubscriptionUpgrade
  const canSeeNormalUpsell = !isFreeTrial && !!canUpsell && !canSubscriptionUpgrade

  const { data: moduleData, loading } = useGetDatoModules({
    locale: datoFormatLocale,
    slug: slug,
    countryCode: countryCodeForCompany,
  })

  const isAlreadyInstalled = Boolean(moduleData[0]?.alreadyPurchased)
  const [currentUpsellOrigin, setUpsellOrigin] = useLocalStorageWithExpiration<{
    request_source: string
    request_source_category?: string
    request_source_component?: string
  }>(UPSELL_ORIGIN_STORAGE_KEY, 5)
  const setUpsellOriginRef = useRef(setUpsellOrigin)
  setUpsellOriginRef.current = setUpsellOrigin

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const upsellOriginParam = queryParams.get('upsell_origin')

    if (upsellOriginParam) {
      let upsellOriginData: {
        request_source: string
        request_source_category?: string
        request_source_component?: string
      } | null = null

      try {
        const parsed = JSON.parse(decodeURIComponent(upsellOriginParam))
        if (parsed && parsed.request_source) {
          upsellOriginData = {
            request_source: parsed.request_source,
            request_source_category: parsed.request_source_category,
            request_source_component: parsed.request_source_component,
          }
        }
      } catch {
        upsellOriginData = { request_source: upsellOriginParam }
      }

      if (upsellOriginData) {
        const hasChanged =
          currentUpsellOrigin?.request_source !== upsellOriginData.request_source ||
          currentUpsellOrigin?.request_source_category !==
            upsellOriginData.request_source_category ||
          currentUpsellOrigin?.request_source_component !==
            upsellOriginData.request_source_component

        if (hasChanged) {
          setUpsellOriginRef.current(upsellOriginData)
        }
      }
    }
  }, [
    location.search,
    currentUpsellOrigin?.request_source,
    currentUpsellOrigin?.request_source_category,
    currentUpsellOrigin?.request_source_component,
  ])

  const moduleChargebeeName = moduleData[0]?.chargebeeModuleName

  useEffect(() => {
    if (canSeeSelfUpsell && isAuthorized && !isAlreadyInstalled && moduleChargebeeName) {
      track(trackingEvents.marketplaceModules.viewSelfUpsellButton, {
        module: moduleChargebeeName,
        from: 'marketplace_resource_header',
      })
    }
  }, [canSeeSelfUpsell, isAuthorized, isAlreadyInstalled, moduleChargebeeName])

  if (loading || moduleData.length === 0) return <Loading />

  const module = moduleData.at(0)

  if (!module) {
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )
  }

  const crossSellingAddonReference = module?.crossSellingBillingAddonReference || []

  const shouldShowTrainingsWidget =
    module?.chargebeeModuleName === 'trainings' &&
    crossSellingAddonReference &&
    crossSellingAddonReference.length > 0 &&
    !isAlreadyInstalled

  return (
    <SelfUpsellProvider
      availableAddons={module?.billingAddonReference || []}
      availableCrossSellingAddons={module?.crossSellingBillingAddonReference || []}
      availableMandatoryCrossSellingAddons={module?.mandatoryCrossSellingAddons || []}
      availableOneTimeBillingCharges={[]}
      moduleName={module?.chargebeeModuleName || ''}
    >
      <ResponsivePage.Body variant="fullscreen">
        <TwoColumnLayout
          sticky
          sideContent={
            <SidePanel
              pitchDeck={module?.downloadInfo?.url ? [{ url: module.downloadInfo.url }] : undefined}
              module={module?.chargebeeModuleName || undefined}
              isAuthorized={isAuthorized}
              setupGuide={module?.setupGuide}
              benefit={module?.benefit}
              moduleName={module?.name || ''}
              navatticImage={module?.navatticImage?.url || module?.carousel?.[0]?.url || ''}
              navatticUrl={module?.navatticUrl || ''}
              navatticTitle={module?.navatticTitle || ''}
              moduleSlug={module?.slug || ''}
            />
          }
        >
          {module?.bannerCallout && (
            <Box paddingBottom={'s40'}>
              <BaseBanner
                title={module?.bannerCallout?.title || ''}
                subtitle={module?.bannerCallout?.description || ''}
                mediaUrl={optimizeImageUrl(module?.bannerCallout?.image?.url || '')}
              />
            </Box>
          )}

          {shouldShowTrainingsWidget && (
            <Box
              paddingBottom={'s40'}
              style={{ marginTop: '-30px', marginLeft: '-40px', marginRight: '-40px' }}
            >
              <TrainingsBanner
                chargebeeModuleName={module?.chargebeeModuleName || ''}
                moduleName={module?.translatedName || ''}
                moduleSlug={module?.slug || ''}
              />
            </Box>
          )}

          <ModuleCarousel
            video={module?.externalUrl}
            images={module?.carousel || []}
            moduleSlug={module?.slug || ''}
          />
          <FeatureBlocks
            features={module?.values || []}
            module={module?.translatedName || ''}
            pitchDeck={module?.downloadInfo?.url ? [{ url: module.downloadInfo.url }] : undefined}
          />
          <PricingPlan
            addonReference={module?.billingAddonReference || []}
            module={module?.slug || undefined}
            isAuthorized={isAuthorized}
            chargebeeModuleName={module?.chargebeeModuleName || ''}
            moduleData={module || undefined}
            canSeeSelfUpsell={canSeeSelfUpsell}
            canSeeNormalUpsell={canSeeNormalUpsell}
          />
          <ClientReviews reviews={module?.testimonials} />
          <LatestUpdates
            module={module?.chargebeeModuleName || ''}
            moduleName={module.name || ''}
            moduleId={module?.id || ''}
          />
        </TwoColumnLayout>
      </ResponsivePage.Body>
    </SelfUpsellProvider>
  )
}

export default ModulesDetailRevamp
